import { ELoginState, IUserState } from '~/providers/interfaces';
import { TLD, BASENAME_PATH, EMAIL } from '~/const';
import { setAnalyticsConfig } from '~/providers/dot';
import { v4 as uuidv4 } from 'uuid';
import { instanceId } from '~/utils/sentry';
import { userStore } from "~/stores/user";
import { apiMe, apiGetProjects } from "~/providers/api";
import { langStore } from "~/stores/lang";
import { getBrowserLang } from "./lang";

export interface IAuthData {
	avatarUrl: string;
	name: string;
	state: ELoginState;
	status: number;
	userId: number;
	userName: string;
	userPublicId: string;
	bankId: boolean;
	accountDisplayName: string;
}

export interface IAuthResponse {
	data: IAuthData;
	error: boolean;
}

// za jak dlouho se ma opakovat auth volani
const AUTH_INTERVAL = 61000;

const LOGIN_CONFIG = {
	serviceId: 'mapy',
	tld: TLD,
};

// sem se ulozi reference na timeout po volani setTimeout - window.setTimeout vraci number!
let authInterval = 0;
let loginWasConfigured = false;

export function setLoginConfig() {
	if (!loginWasConfigured && window.login) {
		window.login.cfg({
			...LOGIN_CONFIG,
			lang: langStore.getState().data.lang,
			...TLD === "dev.dszn.cz"
				? {
					// eslint-disable-next-line
					log: console.log,
				}
				: {},
		});
		loginWasConfigured = true;
	}
}

export function updateLoginLang(lang: string) {
	window.login && window.login.cfg({
		...LOGIN_CONFIG,
		lang,
	});
}

function clearUser() {
	const { unsetUser } = userStore.getState();

	unsetUser();
	setAnalyticsConfig({
		rus: '',
		rusId: 0,
		loginState: ELoginState.Red,
		impress: false,
	});

	if (authInterval) {
		window.clearInterval(authInterval);
		authInterval = 0;
	}
}

async function loadAuth(): Promise<IAuthResponse> {
	try {
		const correlationId = uuidv4();
		const response = await fetch(`${BASENAME_PATH}auth?${Date.now()}`, {
			headers: {
				'Accept': 'application/json',
				'X-Correlation-Id': correlationId,
				'X-Instance-Id': instanceId,
			},
			credentials: 'include',
		});
		const json = await response.json() as IAuthData;

		if (json.status.toString().charAt(0) === '2') {
			return {
				data: json,
				error: false,
			};
		}
	} catch (exc) {
		//
	}

	return {
		data: null,
		error: true,
	};
}

async function auth() {
	const { user: userData, setUser, setUserLoad } = userStore.getState();
	const setLang = langStore.getState().setLang;
	const { data: json, error } = await loadAuth();
	const lang = getBrowserLang();

	/* eslint-disable no-magic-numbers */
	// uzivatel se zmenil
	if (!error && (json.userId !== userData.rusId || json.state !== userData.state)) {
		setUserLoad(false, "");
		const meData = await apiMe(lang);

		if (meData.noErrors) {
			const projectsData = await apiGetProjects("client", meData.data.userLanguage);
			const userStateData: IUserState = {
				name: json.name,
				email: json.userName,
				rusId: json.userId,
				state: json.state,
				avatar: json.avatarUrl,
				publicId: json.userPublicId,
				internalRole: meData.data.internalRole,
				isInternal: meData.data.isInternal,
				// debug pro me - roman.makudera
				hasProjects: json.userName === EMAIL || (projectsData.noErrors
					? projectsData.data.length > 0
					: false),
				userLanguage: meData.data.userLanguage,
			};

			setLang(meData.data.userLanguage, true);
			setUserLoad(true, "");
			setUser(userStateData);

			if (authInterval === 0) {
				authInterval = window.setInterval(function interval() {
					auth();
				}, AUTH_INTERVAL);
			}

			setAnalyticsConfig({
				rus: json.userName || '',
				rusId: json.userId || 0,
				loginState: json.state,
				impress: false,
			});
		} else {
			setLang(lang, true);
			setUserLoad(true, meData.errors.join(", ").trim());
			clearUser();
		}
	} else if (error) {
		setUserLoad(true, "");
		clearUser();
	}
}

export function loginInit() {
	setLoginConfig();

	function sendAuth() {
		auth();
	}

	window.addEventListener('login-done', sendAuth);
	window.addEventListener('login', sendAuth);
	window.addEventListener('logout', sendAuth);
	window.addEventListener('forget', sendAuth);

	auth();
}
